export interface ClientPortalLocationContext {
  readonly clientLinkId: string;
  readonly token: string;
}

export type ClientPortalLinkResolution =
  | { readonly kind: "ready"; readonly clientLinkId: string; readonly token: string }
  | { readonly kind: "missing_token"; readonly clientLinkId: string }
  | { readonly kind: "invalid_path" };

function readToken(search: string, hash: string): string {
  const hashParams = hash.startsWith("#") ? new URLSearchParams(hash.slice(1)) : new URLSearchParams();
  const searchParams = new URLSearchParams(search);
  return hashParams.get("token") ?? searchParams.get("token") ?? "";
}

export function resolveClientPortalLink(
  pathname: string,
  search: string,
  hash = "",
): ClientPortalLinkResolution {
  const match = /^\/client\/([^/]+)$/.exec(pathname);
  if (!match) {
    return { kind: "invalid_path" };
  }
  const token = readToken(search, hash);
  const clientLinkId = decodeURIComponent(match[1] ?? "");
  if (token.length < 32) {
    return { kind: "missing_token", clientLinkId };
  }
  return { kind: "ready", clientLinkId, token };
}

export function resolveClientPortalLocation(
  pathname: string,
  search: string,
  hash = "",
): ClientPortalLocationContext | null {
  const resolved = resolveClientPortalLink(pathname, search, hash);
  if (resolved.kind !== "ready") {
    return null;
  }
  return { clientLinkId: resolved.clientLinkId, token: resolved.token };
}

/** Hash-less refresh can resume V10 from a stored session without the raw token. */
export function clientPortalSessionLocation(input: {
  readonly resolution: ClientPortalLinkResolution;
  readonly sessionToken: string | null;
}): ClientPortalLocationContext | null {
  if (input.resolution.kind === "ready") {
    return { clientLinkId: input.resolution.clientLinkId, token: input.resolution.token };
  }
  if (input.resolution.kind === "missing_token" && input.sessionToken) {
    return { clientLinkId: input.resolution.clientLinkId, token: "" };
  }
  return null;
}
